import { GlobalSearch } from '@bbl-digital/snorre-v2'
import { useGlobalSearch } from './hooks/useGlobalSearch'
import { GlobalSearchContext } from './context/GlobalSearchContext'
import { MenuSearchHits } from './components/MenuSearchHits'
import { BoardMeetingSearchHits } from './components/BoardMeetingSearchHits'
import { InvoiceSearchHits } from './components/InvoiceSearchHits'
import { PreviousSearches } from './components/PreviousSearches'
import { usePreviousSearches } from './hooks/usePreviousSearches'
import { useRef, useState } from 'react'
import { AccountSearchHits } from './components/AccountSearchHits'

const GlobalSearchHandler = () => {
  const [override, setOverride] = useState<string>('')
  const { data, loading, error, query, handleSearchChange, resetSearch } =
    useGlobalSearch()
  const { searches, setPreviousSearch, handleClear } = usePreviousSearches()

  const searchResultRef = useRef<HTMLUListElement>(null)

  const menuHits = data?.menuItems
  const invoiceHits = data?.invoices
  const boardMeetingHits = data?.boardMeetingCases
  const accountHits = data?.accounts

  const hasSomeHits =
    !!menuHits?.hits?.length ||
    !!invoiceHits?.hits?.length ||
    !!boardMeetingHits?.hits?.length ||
    !!accountHits?.hits?.length

  const hasQuery = !!query?.length
  const showPreviousSearches = !loading && !hasQuery
  const showEmpty = !loading && !hasSomeHits && hasQuery
  const showResults = !loading && hasSomeHits && hasQuery
  const showFooter = loading || hasSomeHits

  const handleSetPreviousSearch = (displayName: string) => {
    const search = searches.find((search) => search.displayName === displayName)

    if (!search) return

    // Clear the old search and add it as the latest
    handleClear(search.id)
    setPreviousSearch(search.displayName)

    // Override the current query
    setOverride(search.displayName)
  }

  const onSearchChange = (query: string) => {
    if (override) {
      setOverride('')
    }
    handleSearchChange(query)
  }

  const handleClose = () => {
    if (override) setOverride('')
    resetSearch()
  }

  return (
    <GlobalSearchContext.Provider
      value={{
        error,
        loading,
        menuHits,
        boardMeetingHits,
        invoiceHits,
        accountHits,
      }}
    >
      <GlobalSearch
        query={query}
        overrideQuery={override}
        handleSearch={onSearchChange}
        onClose={handleClose}
        placeholder="Søk i portalen"
      >
        {showPreviousSearches && (
          <PreviousSearches onClick={handleSetPreviousSearch} />
        )}

        {showEmpty && <GlobalSearch.Empty />}
        {showResults && (
          <GlobalSearch.Result ref={searchResultRef}>
            <MenuSearchHits />
            <BoardMeetingSearchHits />
            <InvoiceSearchHits />
            <AccountSearchHits />
          </GlobalSearch.Result>
        )}
        {loading && <GlobalSearch.Loading amount={3} />}
        {showFooter && <GlobalSearch.Footer />}
      </GlobalSearch>
    </GlobalSearchContext.Provider>
  )
}

export default GlobalSearchHandler
